import React, { useState, useEffect, useContext } from 'react';
import classNames from 'classnames';
import get from 'lodash/get';

import Input from '../../components/UI/CustomInputField/index.js';
import Layout from '../../components/Layout/Layout';
import Button from '../../components/UI/Button/index';
import Message from '../../components/UI/Message';
import Testimonial from '../../components/UI/Testimonial/index';
import PromotionCta from '../../components/UI/PromotionCta/index';
import css from './ConfirmCode.module.scss';
import layoutCSS from '../../components/Layout/Layout.module.scss';

import { AnalyticsContext } from '../../ModularReduxAnalytics';

import { useApp, useAppDispatchers } from '../../state/app';
import { useUser, useUserDispatchers } from '../../state/user';
import { useToggles } from '../../state/toggles';
import { planPrices } from '../../utils/prices.ts';

const codeLength = 6;

const ConfirmCode = props => {
  const { analytics } = useContext(AnalyticsContext);
  const {
    tempPhoneNumber,
    role,
    deviceToken,
    partner,
    needsAccessRequest,
    promotionId,
    firstName
  } = useUser();
  const {
    message,
    phoneNumberProvided,
    proveMobileAuthState,
    loading,
    error,
    showFccPreReg
  } = useApp();
  const { accessRequest, voicePin, confirmCode } = useAppDispatchers();
  const { setNextRegStep } = useUserDispatchers();
  const { featureToggles, enableCorePriceChange, enableGSRChange } =
    useToggles();
  const maxFlow =
    get(featureToggles, 'planFlow.enabled') &&
    get(featureToggles, 'planFlow.planName') === 'max';
  const plans = planPrices({ enableGSRChange, enableCorePriceChange });

  const [code, setCode] = useState('');
  const [submittedConfirmCode, setSubmittedConfirmCode] = useState('');
  const [displayTooShortMessage, setDisplayTooShortMessage] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    analytics.trackEvent('Confirm Code Viewed', {
      eventProps: {
        during_registration: true
      }
    });
    if (!deviceToken || !tempPhoneNumber) {
      setNextRegStep('phone');
    } else if (needsAccessRequest) {
      accessRequest({
        phoneNumber: tempPhoneNumber,
        deviceUuid: deviceToken,
        pinLength: codeLength
      });
    }
  }, []);

  useEffect(() => {
    if (code.length === codeLength || code.length === 0) {
      setDisplayTooShortMessage(false);
      setConfirmCode();
    } else {
      setDisplayTooShortMessage(true);
    }
  }, [code]);

  const onChange = value => {
    if (/^\d*$/.test(value)) {
      setCode(value);
    }
  };

  const setConfirmCode = e => {
    if (e) {
      e.preventDefault();
    }
    if (code.length === codeLength && submittedConfirmCode !== code) {
      setSubmittedConfirmCode(code);
      analytics.trackEvent('Confirmation Code Entered', {
        eventProps: {
          code: parseInt(code, 10)
        }
      });

      confirmCode({
        phoneNumber: tempPhoneNumber,
        deviceUuid: deviceToken,
        code
      });
    }
  };

  const requestCall = e => {
    e.preventDefault();
    analytics.trackEvent('Voice Confirm Code Requested');
    voicePin({ phoneNumber: tempPhoneNumber, deviceUuid: deviceToken });
  };

  let testimonial = (
    <Testimonial
      quote="I love Greenlight. As a mom of 4 kids, it makes paying allowances and spending money for trips so easy. I can make sure they have the money they need for whatever might come up!"
      author="Samantha Ballard"
      location="Pigeon Forge, TN"
    />
  );

  if (maxFlow) {
    testimonial = (
      <Testimonial
        quote="I love this card and app... This card let's them learn how to save with a Savings Account, and they can even invest. Super great card and super great app. Thanks Greenlight!!!"
        author="Ashley Windham"
        location="Android GL customer"
        design="max"
        data-testid="max-testimonial"
      />
    );
  }

  if (showFccPreReg) {
    testimonial = (
      <Testimonial
        quote="This has been amazing for us! Traditional banks don't make it nearly as easy. Thank you!"
        author="— Greenlight mom"
      />
    );
  }

  const formattedPhoneNumber = tempPhoneNumber
    ? tempPhoneNumber.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')
    : '';

  let header = 'Welcome to Greenlight.';

  if (!phoneNumberProvided || partner !== 'MSB') {
    header = `What's your confirmation code?`;
  } else if (firstName) {
    header = `Welcome to Greenlight, ${firstName}.`;
  }

  if (
    proveMobileAuthState === 'success' &&
    !needsAccessRequest &&
    role !== 'child'
  ) {
    setNextRegStep('register');
  }

  return (
    <div>
      <Layout data-e2e="confirm-code">
        <div className={css.ConfirmCodeMainBox}>
          <div className={layoutCSS.layoutWrapper}>
            {promotionId && (
              <PromotionCta
                promotionId={promotionId}
                ctaType="confirmCode"
                currentFee={plans.standard}
              />
            )}
            <h1 data-e2e="confirm-code-header">{header}</h1>
            {partner === 'MSB' && phoneNumberProvided && (
              <h2
                data-e2e="msb-confirm-code-subheader"
                data-testid="msb-confirm-code-subheader"
              >
                Greenlight is the debit card for kids and money app for
                families. All plans cover the whole family, up to 5 kids.
              </h2>
            )}
            <h2 data-e2e="confirm-code-subheader" className="fs-mask">
              {`We just texted a confirmation code to ${formattedPhoneNumber}.`}
            </h2>
            <Message error={error} text={message} />
            <form
              className={css.ConfirmCodeForm}
              autoComplete="off"
              data-e2e="confirm-code-form"
              onSubmit={setConfirmCode}
            >
              <Input
                id="confirm-code"
                placeholder="Enter confirmation code"
                error={error}
                autoComplete="off"
                autoCorrect="off"
                inputMode="numeric"
                type="tel"
                value={code}
                onChange={value => onChange(value)}
                pattern="[0-9]*"
                maxLength={codeLength}
                data-e2e="confirm-code"
                format={
                  displayTooShortMessage
                    ? `Confirmation code must be at least 6 digits.`
                    : ''
                }
              />
              <br />
              <p>Didn't get a text?</p>
              <a
                className={classNames({
                  child: role === 'child',
                  parent: role !== 'child'
                })}
                onClick={requestCall}
                data-e2e="request-call"
              >
                Receive a phone call instead
              </a>
              <Button
                text="Confirm code"
                loading={loading}
                disabled={code.length !== codeLength}
                onClick={setConfirmCode}
              />
            </form>
          </div>
          {testimonial}
          <div className={css.ConfirmCodeDisclaimer}>
            {'Message and data rates may apply. Visit Greenlight’s '}
            <a target="_blank" href={'https://privacy.greenlight.com/policies'}>
              Privacy Statement
            </a>
            {' & '}
            <a target="_blank" href={'https://greenlight.com/sms-terms'}>
              SMS Terms
            </a>
            {' for details.'}
          </div>
        </div>
      </Layout>
    </div>
  );
};

export default ConfirmCode;
